'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { LayoutGrid, Plus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import type { DashboardResponse } from '@/features/analytics/custom-dashboards';
import {
  CreateDashboardDialog,
  DashboardList,
  useCreateDashboard,
  useCreateFromTemplate,
  useDashboardCount,
  useDashboards,
  useStarterTemplates,
} from '@/features/analytics/custom-dashboards';
import { StarterTemplateCard } from '@/features/analytics/custom-dashboards/components/StarterTemplateCard';
import { useActiveOrganizationId } from '@/hooks/use-active-org-id';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/analytics/')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.dashboards'),
  }),
  component: AnalyticsPage,
});

function AnalyticsPage() {
  const { t } = useTranslation('analytics');
  const navigate = useNavigate();
  const { organizationId: orgId } = useActiveOrganizationId();
  const [createOpen, setCreateOpen] = useState(false);

  const { data: dashboards, isLoading: dashboardsLoading } = useDashboards(orgId);
  const { data: countData } = useDashboardCount(orgId);
  const { data: templateData, isLoading: templatesLoading } = useStarterTemplates(orgId);
  const createFromTemplate = useCreateFromTemplate(orgId);

  const createMutation = useCreateDashboard(orgId, {
    onSuccess: (dashboard) => {
      toast.success(t('analytics:toast.dashboardCreated'));
      void navigate({
        to: '/analytics/dashboards/$dashboardId',
        params: { dashboardId: dashboard.id },
      });
    },
    onError: (error) => {
      const message = (error as { message?: string }).message ?? 'Failed to create dashboard';
      toast.error(message);
    },
  });

  const handleCreateSubmit = async (data: {
    name: string;
    description?: string | undefined;
    duplicateFromId?: string | undefined;
  }) => {
    await createMutation.mutateAsync(data);
  };

  const hasDashboards = (dashboards?.items.length ?? 0) > 0;

  const handleSelect = (dashboard: DashboardResponse) => {
    void navigate({
      to: '/analytics/dashboards/$dashboardId',
      params: { dashboardId: dashboard.id },
    });
  };

  const goToManage = () => {
    void navigate({ to: '/analytics/dashboards' });
  };

  return (
    <div className="min-h-screen bg-background p-6 lg:p-8">
      <div className="mx-auto max-w-[1400px] space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LayoutGrid className="h-7 w-7 text-primary" />
            <h1 className="text-3xl font-semibold text-foreground">{t('dashboardsPage.title')}</h1>
          </div>
          <Button onClick={() => { setCreateOpen(true); }} data-testid="new-dashboard-btn">
            <Plus className="mr-2 h-4 w-4" />
            {t('dashboardsPage.newDashboard')}
          </Button>
        </div>

        {/* Your Dashboards */}
        {(hasDashboards || dashboardsLoading) && (
          <section>
            <h2 className="mb-4 text-lg font-medium text-foreground">{t('dashboardsPage.yourDashboards')}</h2>
            <DashboardList
              dashboards={dashboards?.items ?? []}
              isLoading={dashboardsLoading}
              onSelect={handleSelect}
              onDuplicate={goToManage}
              onDelete={goToManage}
              onShare={goToManage}
              onCreate={() => { setCreateOpen(true); }}
            />
          </section>
        )}

        {/* Starter Templates */}
        <section>
          <h2 className="mb-4 text-lg font-medium text-foreground">{t('dashboardsPage.startFromTemplate')}</h2>
          {templatesLoading ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="h-48 animate-pulse rounded-lg border bg-muted/50"
                />
              ))}
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {templateData?.templates.map((template) => (
                <StarterTemplateCard
                  key={template.id}
                  template={template}
                  onUse={(id) => { createFromTemplate.mutate({ templateId: id }); }}
                  isLoading={createFromTemplate.isPending}
                />
              ))}
            </div>
          )}
        </section>
      </div>

      <CreateDashboardDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSubmit={handleCreateSubmit}
        isSubmitting={createMutation.isPending}
        dashboardCount={countData?.count}
        dashboardLimit={countData?.limit}
      />
    </div>
  );
}
